import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { maybeHasSession } from "@/lib/session-hint";
import tfLogo from "@/assets/tf-logo-96.png.asset.json";

const NAV = [
  { to: "/pricing", label: "Pricing" },
  { to: "/method", label: "Method" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
] as const;

// Secondary links live in the footer: Founders, Glossary, FAQ, Security.
const SECONDARY = [
  { to: "/founders", label: "Founders" },
  { to: "/glossary", label: "Glossary" },
  { to: "/faq", label: "FAQ" },
  { to: "/security", label: "Security" },
] as const;


export function SiteHeader() {
  const [signedIn, setSignedIn] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setSignedIn(maybeHasSession());
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="sticky top-0 z-30 border-b border-border/60 bg-background/70 backdrop-blur-xl">
      {!signedIn && (
        <div className="border-b border-border/50 bg-primary/[0.07]">
          <Link
            to="/auth"
            search={{ beta: "1" }}
            className="mx-auto flex w-full max-w-6xl items-center justify-center gap-2 px-4 py-2 text-center text-[0.7rem] tracking-[0.14em] text-muted-foreground uppercase transition-colors hover:text-foreground sm:px-10"
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
            <span className="truncate">Free beta seats are open — no card, every feature</span>
            <span className="shrink-0 text-foreground">Take one →</span>
          </Link>
        </div>
      )}
      <div className="mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 sm:px-10 sm:py-4">

        <Link to="/" className="flex min-w-0 items-center gap-3" onClick={() => setOpen(false)}>
          <img
            src={tfLogo.url}
            alt="The Founders App logo"
            className="h-7 w-7 shrink-0 rounded-sm"
            width={28}
            height={28}
          />
          <span className="micro-label min-w-0 truncate text-foreground">The Founders App</span>
          <span className="hidden shrink-0 whitespace-nowrap text-[0.625rem] tracking-[0.24em] text-muted-foreground/60 uppercase xl:inline">
            Private operating system for founders
          </span>
        </Link>

        <nav className="hidden items-center gap-5 text-xs lg:flex">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="micro-label transition-colors hover:text-foreground"
              activeProps={{ className: "text-foreground" }}
            >
              {item.label}
            </Link>
          ))}
          <span className="h-4 w-px bg-border" aria-hidden="true" />
          {!signedIn && (
            <Link
              to="/auth"
              className="micro-label transition-colors hover:text-foreground"
            >
              Sign in
            </Link>
          )}
          <Link
            to={signedIn ? "/app/today" : "/auth"}
            {...(!signedIn ? { search: { beta: "1" } } : {})}
            className="micro-label rounded-sm border border-foreground/70 bg-foreground/10 px-3.5 py-2 whitespace-nowrap text-foreground transition-colors hover:bg-foreground/20"
          >
            {signedIn ? "Enter" : "Free beta seat"}
          </Link>

        </nav>

        <div className="shrink-0 lg:hidden">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="site-mobile-nav"
            className="flex h-10 w-10 items-center justify-center rounded-sm border border-border text-muted-foreground transition-colors hover:text-foreground"
          >
            {open ? (
              <X className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Menu className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="site-mobile-nav"
          className="border-t border-border/60 bg-background/95 px-4 py-3 backdrop-blur-xl lg:hidden"
        >
          <ul className="grid grid-cols-2 gap-1">
            {NAV.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="micro-label flex min-h-11 items-center rounded-sm px-3 transition-colors hover:bg-secondary hover:text-foreground"
                  activeProps={{ className: "text-foreground" }}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            {SECONDARY.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="flex min-h-11 items-center rounded-sm px-3 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  activeProps={{ className: "text-foreground" }}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-3 grid gap-2 border-t border-border/60 pt-3">
            {!signedIn && (
              <Link
                to="/auth"
                onClick={() => setOpen(false)}
                className="micro-label flex min-h-11 items-center justify-center rounded-sm border border-border transition-colors hover:text-foreground"
              >
                Sign in
              </Link>
            )}
            <Link
              to={signedIn ? "/app/today" : "/auth"}
              {...(!signedIn ? { search: { beta: "1" } } : {})}
              onClick={() => setOpen(false)}
              className="micro-label flex min-h-11 items-center justify-center rounded-sm border border-foreground/70 bg-foreground/10 text-foreground transition-colors hover:bg-foreground/20"
            >
              {signedIn ? "Enter app" : "Free beta seat"}
            </Link>
          </div>

        </nav>
      )}
    </header>
  );
}
